/** Host OS — Billing & payouts. Gross earnings are real; payout scheduling is a preview. */
import { useCallback, useEffect, useState } from "react";
import {
  Wallet, Loader2, Landmark, ArrowDownToLine, ShieldCheck, Clock, Info,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, PreviewBadge, currencySymbol } from "@/components/host/hostUi";

const PLATFORM_FEE = 0.05; // 5% illustrative

const HostBillingPage = () => {
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/host/revenue?months=12`, { headers });
      if (res.ok) setRev(await res.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const lifetime = rev?.lifetime_total_by_currency || {};
  const currencies = Object.keys(lifetime);

  return (
    <HostLayout>
      <div data-testid="host-billing-page">
        <HostPageHeader
          icon={Wallet}
          title="Billing"
          subtitle="Track gross earnings and manage how you get paid out."
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            {/* Balance cards (real gross earnings) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {currencies.length === 0 ? (
                <Card className="p-6 sm:col-span-3 text-center text-muted-foreground" data-testid="billing-empty">
                  No earnings yet — your available balance will appear here after your first paid booking.
                </Card>
              ) : currencies.map((cur) => {
                const gross = lifetime[cur] || 0;
                const fee = gross * PLATFORM_FEE;
                const net = gross - fee;
                return (
                  <Card key={cur} className="p-4" data-testid={`billing-balance-${cur}`}>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Available · {cur}</p>
                    <p className="font-display text-3xl">{currencySymbol(cur)}{Math.round(net).toLocaleString()}</p>
                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                      <div className="flex justify-between"><span>Gross earnings</span><span>{currencySymbol(cur)}{Math.round(gross).toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>Platform fee (5%)</span><span>−{currencySymbol(cur)}{Math.round(fee).toLocaleString()}</span></div>
                    </div>
                    <Button size="sm" className="w-full mt-4 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`billing-payout-${cur}`}>
                      <ArrowDownToLine className="w-4 h-4" /> Withdraw
                    </Button>
                  </Card>
                );
              })}
            </div>

            {/* Payout method */}
            <Card className="p-5" data-testid="billing-payout-method">
              <div className="flex items-center gap-2 mb-1">
                <Landmark className="w-4 h-4 text-primary-ink" />
                <h2 className="font-display text-xl">Payout method</h2>
                <PreviewBadge />
              </div>
              <p className="text-sm text-muted-foreground mb-4">Connect a bank account or Stripe payout destination to receive automatic transfers.</p>
              <div className="flex items-center justify-between gap-3 p-4 rounded-lg border border-dashed border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">No payout method connected</p>
                    <p className="text-xs text-muted-foreground">Add a destination to enable withdrawals</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" data-testid="billing-connect">Connect</Button>
              </div>
            </Card>

            {/* Payout schedule + history (preview) */}
            <div className="grid lg:grid-cols-2 gap-3">
              <Card className="p-5" data-testid="billing-schedule">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-primary-ink" />
                  <h3 className="font-display text-lg">Payout schedule</h3>
                  <PreviewBadge />
                </div>
                <p className="text-sm text-muted-foreground mb-3">Choose how often earnings are transferred once a method is connected.</p>
                <div className="space-y-2">
                  {["Weekly · every Monday", "Monthly · 1st of the month", "Manual · withdraw anytime"].map((opt, i) => (
                    <label key={opt} className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-not-allowed opacity-80">
                      <input type="radio" name="schedule" defaultChecked={i === 0} disabled className="accent-current" />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              </Card>

              <Card className="p-5" data-testid="billing-history">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-primary-ink" />
                  <h3 className="font-display text-lg">Payout history</h3>
                  <PreviewBadge />
                </div>
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Info className="w-6 h-6 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No payouts yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Completed transfers and invoices will be listed here.</p>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </HostLayout>
  );
};

export default HostBillingPage;
